import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHIP_TYPES = ["FB", "CH", "CB", "SL"];
const CHIP_VALUES = [1, 2, 3];
const DEFAULT_TIMEOUT_SECS = 20;
// Default combo when timer expires — always a valid 1·1·2·3 assignment.
const DEFAULT_VALUES = { FB: 1, CH: 1, CB: 2, SL: 3 };

// Valid combo: two 1s, one 2, one 3 — sorted = [1,1,2,3]
const isValidCombo = (vals) => {
  const filled = CHIP_TYPES.map(c => vals[c]).filter(v => v != null);
  if (filled.length !== 4) return false;
  const sorted = [...filled].sort((a, b) => a - b);
  return sorted[0] === 1 && sorted[1] === 1 && sorted[2] === 2 && sorted[3] === 3;
};

export default function BluffValuesModal({ gameId, role, pitcher, pitcherIndex = 1, totalPitchers = 1, teamName, noTimers = false, onDone, timeoutSecs }) {
  const effectiveTimeout = timeoutSecs > 0 ? timeoutSecs : DEFAULT_TIMEOUT_SECS;
  const [values, setValues] = useState({ FB: null, CH: null, CB: null, SL: null });
  const [saving, setSaving] = useState(false);
  const [timeLeft, setTimeLeft] = useState(effectiveTimeout);
  const autoFiredRef = useRef(false);

  const countOf = (v) => CHIP_TYPES.filter(c => values[c] === v).length;
  const allSet = isValidCombo(values);

  // Reset timer whenever the pitcher changes (multi-pitcher flow).
  // No Timers mode: skip the countdown entirely — the player must assign manually.
  useEffect(() => {
    autoFiredRef.current = false;
    if (noTimers) return;
    setTimeLeft(effectiveTimeout);
    const id = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          if (!autoFiredRef.current) {
            autoFiredRef.current = true;
            submitValues(DEFAULT_VALUES);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pitcher?.id, noTimers]);

  // 2 and 3 are exclusive (one each); 1 can appear on at most two chips.
  const isDisabled = (chip, v) => {
    if (values[chip] === v) return false; // clicking own value = toggle off, always allowed
    if (v === 1) return countOf(1) >= 2;
    return CHIP_TYPES.some(c => c !== chip && values[c] === v);
  };

  const assign = (chip, v) => {
    setValues(prev => {
      const next = { ...prev };
      if (v !== 1) {
        // 2 and 3 are exclusive — displace any other chip holding this value
        for (const c of CHIP_TYPES) {
          if (c !== chip && next[c] === v) next[c] = null;
        }
      }
      next[chip] = next[chip] === v ? null : v;
      return next;
    });
  };

  const submitValues = async (vals) => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/games/${gameId}/pitcher-chip-values`, {
        role,
        pitcher_id: pitcher.id,
        chip_values: { FB: vals.FB, CH: vals.CH, CB: vals.CB, SL: vals.SL },
      });
      onDone({ FB: vals.FB, CH: vals.CH, CB: vals.CB, SL: vals.SL });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to save chip values — try again");
      setSaving(false);
    }
  };

  const handleConfirm = () => allSet && !saving && submitValues(values);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Got Your Number — assign your secret chip values"
      data-testid="gyn-assign-modal"
      className="fixed inset-0 z-[1200] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-slate-950 border border-amber-500/40 rounded-2xl shadow-2xl max-w-sm w-full p-5 space-y-4">
        <div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-black tracking-widest text-amber-400 uppercase">Got Your Number — Keep Secret</p>
            <div className="flex items-center gap-2">
              {totalPitchers > 1 && (
                <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  {pitcherIndex} / {totalPitchers}
                </p>
              )}
              {!noTimers && (
                <span className={`text-xs font-black tabular-nums ${timeLeft <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
                  {timeLeft}s
                </span>
              )}
            </div>
          </div>
          {teamName && <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">{teamName}</p>}
          <p className="font-black text-white text-base mt-1">{pitcher.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Assign 1·1·2·3 across your pitches. Chip match → batter wins the higher of both values. Wrong guess → pitcher wins it.
          </p>
          {totalPitchers > 1 && pitcherIndex === 1 && (
            <p className="text-[10px] text-amber-500/70 mt-1.5">Assign values for all your pitchers before handing the device to your opponent.</p>
          )}
        </div>

        <div className="grid grid-cols-4 gap-2">
          {CHIP_TYPES.map(chip => (
            <div key={chip} className="space-y-1">
              <p className="text-[10px] font-black text-center text-slate-400 uppercase">{chip}</p>
              <div className="space-y-1">
                {CHIP_VALUES.map(v => {
                  const assigned = values[chip] === v;
                  const disabled = isDisabled(chip, v);
                  return (
                    <button
                      key={v}
                      onClick={() => assign(chip, v)}
                      disabled={disabled}
                      className={`w-full py-1.5 rounded-lg text-sm font-black transition-colors ${
                        assigned
                          ? "bg-amber-500 text-black"
                          : disabled
                            ? "bg-slate-800/30 text-slate-700 cursor-not-allowed"
                            : "bg-slate-800 text-slate-400 hover:bg-slate-700 hover:text-white"
                      }`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={handleConfirm}
          disabled={!allSet || saving}
          className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-black text-sm transition-all"
        >
          {saving ? "Saving…" : allSet
            ? (pitcherIndex < totalPitchers ? `Lock In → Next Pitcher (${pitcherIndex + 1}/${totalPitchers})` : "Lock In Values →")
            : "Assign 1·1·2·3 across pitches to continue"}
        </button>
      </div>
    </div>
  );
}
